import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AuthDecisionContext } from '../Routes';
import useDebugMode from '../hooks/useDebugMode';

// ── INLINE DEBUG OVERLAY (PaywallGuard) ───────────────────────────────────
const PaywallDebugOverlay = ({ data }) => {
  const debugMode = useDebugMode();
  const [visible, setVisible] = useState(true);

  if (!debugMode) return null;

  if (!visible) return (
    <button
      onClick={() => setVisible(true)}
      style={{ position: 'fixed', bottom: 8, left: 8, zIndex: 99999, background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer' }}
    >🔒 Paywall</button>
  );
  return (
    <div style={{
      position: 'fixed', top: 44, left: 0, right: 0, zIndex: 99998,
      background: '#1e1b4b', color: '#e2e8f0', padding: '8px 14px',
      fontFamily: 'monospace', fontSize: '11px', borderBottom: '2px solid #7c3aed',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <strong style={{ color: '#a78bfa' }}>🔒 PAYWALL DEBUG</strong>
        <button onClick={() => setVisible(false)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 14 }}>✕</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '2px 16px' }}>
        {Object.entries(data)?.map(([k, v]) => (
          <span key={k}>
            <span style={{ color: '#94a3b8' }}>{k}:</span>{' '}
            <code style={{ color: v === null || v === false || v === 'CHECKING' || v === 'BLOCKED' ? '#f87171' : v === true || v === 'ALLOWED' || v === 'PAID' || v === 'CONTEXT_RESOLVED' ? '#4ade80' : '#fbbf24' }}>
              {v === null ? 'null' : v === undefined ? 'undefined' : String(v)}
            </code>
          </span>
        ))}
      </div>
    </div>
  );
};

// Statuses that are considered "paid/active"
const PAID_STATUSES = ['active', 'trialing'];

// Explicit opt-out for local development or testing — set VITE_DISABLE_PAYWALL=true in .env
const PAYWALL_DISABLED = import.meta.env?.VITE_DISABLE_PAYWALL === 'true';

// Routes exempt from PaywallGuard — setup flows and account management
// needed when subscription is lapsed or missing
const EXEMPT_ROUTES = [
  // Onboarding setup flow
  '/setup-flow-company-details',
  '/setup-flow-plan-confirmation',
  '/setup-flow-create-site',
  '/setup-flow-invite-team',
  '/setup-flow-checklist-confirmation',
  '/setup-flow-language-preferences',
  // Account management — accessible even when subscription lapses
  '/subscription-management',
  '/invoice-history',
  '/profile-settings',
  '/help',
];

const PaywallGuard = ({ children }) => {
  const { user, userProfile, isDemoUser } = useAuth();
  const location = useLocation();
  const authDecision = React.useContext(AuthDecisionContext);

  // Exempt routes are always allowed — never block or redirect them
  // NOTE: This check is AFTER all hook calls to avoid React Rules of Hooks violation.
  const isExemptRoute = EXEMPT_ROUTES?.some(route => location?.pathname?.startsWith(route));

  // ── DECISION LOGIC ────────────────────────────────────────────────────────
  // PaywallGuard now uses AuthDecisionContext (resolved by AuthRouter) as its
  // primary signal. This eliminates the independent companyService.getUserCompany()
  // call that caused auth token lock contention and infinite redirect loops.

  // Exempt routes bypass all paywall checks
  if (isExemptRoute) {
    return <>{children}</>;
  }

  // No auth context yet — fail open (AuthRouter hasn't resolved)
  if (!authDecision) {
    console.warn('[PaywallGuard] No AuthDecisionContext — failing open');
    return (
      <>
        <PaywallDebugOverlay data={{
          guard: 'PaywallGuard',
          state: 'NO_CONTEXT',
          warning: 'AuthDecisionContext not found — failing open',
          pathname: location?.pathname,
          PAYWALL_DISABLED,
        }} />
        {children}
      </>
    );
  }

  // No profile yet — fail open while loading
  if (!userProfile) {
    return (
      <>
        <PaywallDebugOverlay data={{
          guard: 'PaywallGuard',
          state: 'NO_PROFILE',
          pathname: location?.pathname,
        }} />
        {children}
      </>
    );
  }

  // Demo users bypass all checks
  if (isDemoUser) {
    return (
      <>
        <PaywallDebugOverlay data={{
          guard: 'PaywallGuard',
          state: 'BYPASSED_DEMO_USER',
          pathname: location?.pathname,
        }} />
        {children}
      </>
    );
  }

  // Explicit dev/test bypass
  if (PAYWALL_DISABLED) {
    return (
      <>
        <PaywallDebugOverlay data={{
          guard: 'PaywallGuard',
          state: 'BYPASSED_ENV_FLAG',
          PAYWALL_DISABLED,
          pathname: location?.pathname,
        }} />
        {children}
      </>
    );
  }

  // ── USE AUTH ROUTER'S RESOLVED DATA ────────────────────────────────────
  // AuthRouter already queried the company and determined subscriptionActive.
  // Trust that result instead of making a second independent lookup.
  if (authDecision?.subscriptionActive) {
    return (
      <>
        <PaywallDebugOverlay data={{
          guard: 'PaywallGuard',
          state: 'ALLOWED_VIA_AUTH_CONTEXT',
          subscriptionActive: authDecision?.subscriptionActive,
          hasCompany: authDecision?.hasCompany,
          pathname: location?.pathname,
        }} />
        {children}
      </>
    );
  }

  // Subscription not active — defer to AuthRouter's resolved destination.
  // AuthRouter distinguishes never-paid (→ /setup-flow-plan-confirmation) from
  // previously-paid (→ /subscription-management) based on billing state.
  const redirectTarget = authDecision?.destination || '/setup-flow-plan-confirmation';
  console.warn('[PAYWALL_GUARD_REDIRECT] →', redirectTarget, '| subscriptionActive=', authDecision?.subscriptionActive);
  return (
    <>
      <PaywallDebugOverlay data={{
        guard: 'PaywallGuard',
        state: 'BLOCKED_NO_SUBSCRIPTION',
        subscriptionActive: authDecision?.subscriptionActive,
        hasCompany: authDecision?.hasCompany,
        pathname: location?.pathname,
        redirectTo: redirectTarget,
      }} />
      <Navigate to={redirectTarget} replace />
    </>
  );
};

export default PaywallGuard;
